import { useFormState } from 'react-hook-form';

/**
 * Checks if an object is an error object.
 * An error object must have message, type, and ref properties.
 *
 * @param obj - The object to check
 * @returns True if the object is an error object, false otherwise
 */
const isErrorObject = (obj: any): boolean =>
  obj !== null &&
  typeof obj === 'object' &&
  'message' in obj &&
  'type' in obj &&
  'ref' in obj;

/**
 * Counts errors in an object recursively.
 *
 * @param obj - The object to count errors in
 * @returns The number of errors found
 */
const countErrorsRecursively = (obj: any): number => {
  if (!obj) return 0;

  if (isErrorObject(obj)) {
    return 1;
  }

  if (Array.isArray(obj)) {
    return obj.reduce((count, item) => count + countErrorsRecursively(item), 0);
  }

  if (typeof obj === 'object') {
    return Object.values(obj as object).reduce(
      (count, value) => count + countErrorsRecursively(value),
      0,
    );
  }

  return 0;
};

/**
 * Checks if a field matches a path mask.
 *
 * @param field - The field to check
 * @param pathMask - The path mask to match against
 * @returns True if the field matches the path mask, false otherwise
 */
const matchesPathMask = (field: string, pathMask: string): boolean => {
  // If the mask ends with .*, it matches any field that starts with the prefix
  if (pathMask.endsWith('.*')) {
    const prefix = pathMask.slice(0, -2);
    return (
      field === prefix ||
      field.startsWith(`${prefix}.`) ||
      field.startsWith(`${prefix}[`)
    );
  }

  return field === pathMask;
};

/**
 * Custom hook that counts the number of errors in a form.
 * Supports recursive counting in nested objects and arrays,
 * and path masks like 'partners.*'.
 *
 * @param fields - Optional array of field names to count errors for.
 * @returns The number of errors in the form.
 */
export const useFormErrorCounter = (fields?: string[]) => {
  const { errors } = useFormState();

  if (!errors) return 0;

  // If no fields are specified, count all errors recursively
  if (!fields || fields.length === 0) {
    return countErrorsRecursively(errors);
  }

  // Count errors for specified fields, supporting path masks
  let errorCount = 0;

  // Get all error paths as flat strings
  const errorPaths = Object.keys(errors);

  // For each field pattern, find matching error paths and count errors
  fields.forEach(fieldPattern => {
    const matchingPaths = errorPaths.filter(path =>
      matchesPathMask(path, fieldPattern),
    );

    matchingPaths.forEach(path => {
      // Get the error object at the path and count errors recursively
      let errorObj = errors as any;

      // Handle both dot notation and array indices in the path
      const parts = path.match(/[^.[\]]+/g) || [];
      for (const part of parts) {
        if (!errorObj) break;
        errorObj = errorObj[part];
      }

      errorCount += countErrorsRecursively(errorObj);
    });
  });

  return errorCount;
};
